import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import type { PaginatedResult } from '../services/api';

/**
 * Cursor-paged list accumulation ("load more" UX over the API's
 * startAfter cursors). Resets on tenant switch and whenever the caller
 * changes `resetKey` (e.g. active filter). Sequence-guarded: a slow
 * page from a previous key never lands in the current list.
 */
export function useCursorList<T extends { id: string }>(
  fetchPage: (
    tenantId: string,
    startAfter: string | undefined
  ) => Promise<PaginatedResult<T>>,
  resetKey: string
) {
  const { tokenReady } = useAuth();
  const { tenantId } = useTenant();
  const [items, setItems] = useState<T[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<unknown>(null);
  /** Next-page failures are NOT fatal: the loaded rows stay visible and
   *  the retry lives on the load-more button, never a full ErrorState. */
  const [loadMoreError, setLoadMoreError] = useState<unknown>(null);
  const cursorRef = useRef<string | undefined>(undefined);
  const seqRef = useRef(0);

  const loadFirst = useCallback(async () => {
    if (!tokenReady || !tenantId) return;
    const seq = ++seqRef.current;
    setLoading(true);
    // A superseded load-more's finally() skips its own cleanup (stale
    // seq) — the new first-load owns the flags from here.
    setLoadingMore(false);
    setLoadMoreError(null);
    setError(null);
    cursorRef.current = undefined;
    try {
      const page = await fetchPage(tenantId, undefined);
      if (seqRef.current !== seq) return;
      setItems(page.items);
      setHasMore(page.pagination?.hasMore ?? false);
      cursorRef.current = page.pagination?.nextCursor ?? undefined;
    } catch (err) {
      if (seqRef.current !== seq) return;
      setError(err);
      setItems([]);
      setHasMore(false);
    } finally {
      if (seqRef.current === seq) setLoading(false);
    }
  }, [fetchPage, tenantId, tokenReady]);

  const loadMore = useCallback(async () => {
    if (!tenantId || !cursorRef.current) return;
    const seq = seqRef.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const page = await fetchPage(tenantId, cursorRef.current);
      if (seqRef.current !== seq) return;
      setItems((prev) => [...prev, ...page.items]);
      setHasMore(page.pagination?.hasMore ?? false);
      cursorRef.current = page.pagination?.nextCursor ?? undefined;
    } catch (err) {
      if (seqRef.current === seq) setLoadMoreError(err);
    } finally {
      if (seqRef.current === seq) setLoadingMore(false);
    }
  }, [fetchPage, tenantId]);

  useEffect(() => {
    void loadFirst();
    // resetKey participates intentionally: filter changes restart paging.
  }, [loadFirst, resetKey]);

  return {
    items,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMoreError,
    reload: loadFirst,
    loadMore,
  };
}
